import { IAddress, ITableItem, ITag } from 'stores/interfaces';

export class NetworkError extends Error {
  status: number;
  date: Date;

  constructor(status: number, message: string) {
    super(message);

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, NetworkError);
    }

    this.name = 'NetworkError';
    this.status = status;
    this.date = new Date();
  }
}

export interface IInvestment extends ITableItem {
  offering_id?: string;
  offering_name?: string;
  offering_status?: number;
  offering_type?: number;
  promotion_type?: number;
  offering_price?: number;
  offering_quantity?: number;
  created_at?: number;
  updated_at?: number;
  email?: string;
  display_name?: string;
  description?: string;
  asset_type?: string;
  media?: string[];
  sponsor_id?: string;
  sponsor_name?: string;
  sponsor_logo?: string;
  creator_id?: string;
  tag?: ITag;

  geo_location?: LatLon;
  geo_shape?: GeoShape;
  gallery_media?: Array<IGalleryItem>;

  quantity?: number;
  price?: number;
  currency?: string;

  event_status?: number;
  event_type?: number;
  subject?: string;
  main_content?: string;
  sender_email?: string;
  template_id?: string;
  name?: string;
  scheduled_time?: number;

  [key: string]: unknown;
}

export interface IFilterData extends IInvestment {
  quantity_holding_investor?: number;
  volume_holding_investor?: number;
  quantity_executed_int_buy_investor?: number;
  quantity_executed_bid_buy_investor?: number;
  quantity_executed_bid_sell_investor?: number;
  quantity_pending_int_buy_investor?: number;
  quantity_pending_bid_buy_investor?: number;
  quantity_pending_bid_sell_investor?: number;
  watch?: boolean;
}

export interface IOfferingSearch extends IInvestment {
  location: string;
  formatted_place: string;
  description?: string;
  geo_location?: LatLon;
  geo_shape?: GeoShape;
  quantity_holding_investor?: number;
  volume_holding_investor?: number;
  quantity_executed_int_buy_investor?: number;
  quantity_executed_bid_buy_investor?: number;
  quantity_executed_bid_sell_investor?: number;
  quantity_pending_int_buy_investor?: number;
  quantity_pending_bid_buy_investor?: number;
  quantity_pending_bid_sell_investor?: number;
}

export interface IOfferingInfo extends IInvestment {
  price?: number;
  quantity?: number;
  equity?: number;

  acquisition_price?: number;
  acquisition_price_top?: number;
  acquisition_date?: number;
  recap_price?: number;
  recap_price_top?: number;
  recap_date?: number;
  bov_valuation?: number;
  bov_valuation_top?: number;
  bov_valuation_date?: number;
  appraisal_valuation?: number;
  appraisal_valuation_top?: number;
  appraisal_valuation_date?: number;
  implied_valuation?: number;
  implied_cap_rate?: number;
  implied_cap_rate_top?: number;
  total_offering_cost?: number;
  acquisition_cap_rate?: number;
  acquisition_cap_rate_top?: number;
  recap_cap_rate?: number;
  recap_cap_rate_top?: number;
  stabilized_cap_rate?: number;
  stabilized_cap_rate_top?: number;
  return_on_cost?: number;
  return_on_cost_top?: number;
  target_irr?: number;
  target_irr_top?: number;
  target_yield?: number;
  target_yield_top?: number;
  target_equity_multiple?: number;
  target_equity_multiple_top?: number;
  total_quantity?: number;
  buy_price?: number;
  valuation?: number;
  discount?: number;

  max_discount?: number;
  max_premium?: number;
  min_holding_amount?: number;
  min_invest_amount?: number;
  max_invest_amount?: number;
  buyer_fees?: number;
  seller_fees?: number;
  buyer_fees_investor?: number;
  seller_fees_investor?: number;

  t12_noi?: number;
  t12_noi_date?: number;
  t12_distributions?: number;
  t12_yield?: number;
  t12_yield_top?: number;
  strategy?: string;
  target_equity?: number;
  target_equity_top?: number;
  target_aum_size?: number;
  target_aum_size_range?: number;
  investment_types?: string;

  hold_date?: number;
  hold_date_top?: number;
  start_at?: number;
  end_at?: number;
  days_start?: number;
  days_left?: number;
  funding_progress?: number;
  watch?: boolean;
  country?: string;
  state?: string;
  address?: IAddress;

  marketplace_metrics?: string[];
  offering_promo_metrics?: string[];
  offering_metrics?: string[];

  min_price_pending_bid_sell?: number;
  quantity_holding_investor?: number;
  volume_holding_investor?: number;
  volume_pending_int_buy_investor?: number;
  volume_pending_bid_buy_investor?: number;
  volume_pending_bid_sell_investor?: number;
  avg_price_holding_investor?: number;

  quantity_pending_investor_any?: number;
  quantity_pending_int_buy_investor?: number;
  quantity_pending_bid_buy_investor?: number;
  quantity_pending_bid_sell_investor?: number;
  avg_price_pending_bid_buy_investor?: number;
  avg_price_pending_bid_sell_investor?: number;

  volume_pending_int_buy?: number;
  volume_pending_bid_buy?: number;
  volume_pending_bid_sell?: number;

  pending_int_buy_order_price_investor?: number;
  pending_int_buy_order_quantity_investor?: number;
  pending_int_buy_order_status_investor?: number;

  pending_bid_buy_order_price_investor?: number;
  pending_bid_buy_order_quantity_investor?: number;
  pending_bid_buy_order_status_investor?: number;

  pending_bid_sell_order_price_investor?: number;
  pending_bid_sell_order_quantity_investor?: number;
  pending_bid_sell_order_status_investor?: number;

  total_dividends_investor?: number;
  t12_yield_investor?: number;

  sponsor_id?: string;
  sponsor_name?: string;
  investment_highlights?: string;
}

export interface IOfferingBrowsingData extends IOfferingInfo {
  description?: string;
  sponsor_name?: string;
  asset_name?: string;

  quantity_holding?: number;
  quantity_holding_investor?: number;
  volume_holding_investor?: number;

  quantity_executed_int_buy?: number;
  quantity_executed_bid_buy?: number;
  quantity_executed_bid_sell?: number;
  quantity_executed_buy?: number;
  quantity_executed_buy_investor?: number;
  quantity_executed_int_buy_investor?: number;
  quantity_executed_bid_buy_investor?: number;
  quantity_executed_bid_sell_investor?: number;
  quantity_pending_buy?: number;
  quantity_pending_int_buy?: number;
  quantity_pending_bid_buy?: number;
  quantity_pending_bid_sell?: number;
  quantity_pending_int_buy_investor?: number;
  quantity_pending_bid_buy_investor?: number;
  quantity_pending_bid_sell_investor?: number;
  quantity_pending_investor_any?: number;

  avg_price_pending_int_buy?: number;
  avg_price_pending_bid_buy?: number;
  avg_price_pending_bid_sell?: number;
  avg_price_pending_bid_buy_investor?: number;
  avg_price_pending_bid_sell_investor?: number;
  avg_price_executed_buy?: number;
  avg_price_holding_investor?: number;
  avg_price_executed_int_buy_investor?: number;
  avg_price_executed_bid_buy_investor?: number;
  avg_price_executed_bid_sell_investor?: number;

  last_trade_created_at?: number;
  last_trade_quantity?: number;
  last_trade_price?: number;
  last_trade_currency?: string;

  min_pending_bid_sell_quantity?: number;
  min_pending_bid_sell_price?: number;
  min_pending_bid_sell_currency?: string;

  total_shares_sell_investor?: number;
  total_shares_buy_investor?: number;
  total_dividends_investor?: number;

  pending_int_buy_order_price_investor?: number;
  pending_int_buy_order_quantity_investor?: number;
  pending_int_buy_order_status_investor?: number;

  pending_bid_buy_order_price_investor?: number;
  pending_bid_buy_order_quantity_investor?: number;
  pending_bid_buy_order_status_investor?: number;

  pending_bid_sell_order_price_investor?: number;
  pending_bid_sell_order_quantity_investor?: number;
  pending_bid_sell_order_status_investor?: number;
}

export type AggType = 'value' | 'range' | 'date';

export interface IAggField {
  field: string;
  type: AggType;
  name_field?: string;
  ranges?: { from?: number; to?: number }[];
}

export interface IAggRequest {
  [key: string]: IAggField;
}

export interface IAggBucket {
  key: string;
  name?: string;
  count?: number;
  from?: string | number;
  to?: string | number;
  isSelected?: boolean;
}

export interface IFilterOptionsItem {
  keyName?: string;
  filterType?: string;
  noneValue?: boolean;
  filterValues?: IAggBucket[];
}

export interface IFilter {
  field: string;
  value?: string[];
}

export interface ILocationFilter {
  field?: string;
  value?: Envelope;
}

export type LatLon = {
  lat?: number;
  lon?: number;
  lng?: number;
};

export type StreetView = {
  position: LatLon;
  pov: {
    heading?: number;
    pitch?: number;
  };
};

export type Envelope = {
  tlLon: number;
  brLon: number;
  tlLat: number;
  brLat: number;
};

export type GeoShape = {
  coordinates: [];
  type: string;
};

export interface IOfferingTitle {
  offeringInfo?: IOfferingInfo;
  name?: string;
  description?: string;
  type?: string;
  offeringId?: string;
  buyEnabled?: boolean;
  sellEnabled?: boolean;
  withButton?: boolean;
  customClass?: string;
  className?: string;
  redirectPath?: string;
  isAuthenticated?: boolean;
  isApproved?: boolean;
  isManagement?: boolean;
}

// json table

export interface ITableDataHeading {
  key: string;
  className?: string;
}

export interface ITableDataItemValues {
  text?: string;
  title?: string;
  type?: string;
  className?: string;
}

export interface ITableDataItem {
  [key: string]: ITableDataItemValues;
}

export interface ITableData {
  key?: string;
  data?: ITableDataItem[];
  tableHeadings?: ITableDataHeading[];
  comment?: string;

  [key: string]: unknown;
}

export type IResourceData = {
  name?: string;
  description?: string;
  last_modified?: number;
  url?: string;
};

export type IResourceDataList = Array<IResourceData>;

// Gallery
export interface IGalleryItem {
  original?: string;
  thumbnail?: string;
  title?: string;
  width?: number;
  height?: number;
}

export type IGalleryItemList = Array<IGalleryItem>;

// Capital Stack
export interface ICapitalStack {
  stack: {
    title: string;
    color: string;
    percentage?: number;
    comment?: string;
    amount: number;
    priority: number;
  }[];
  total: number;
}

export interface IPerformanceData {
  [key: string]: {
    noi: number;
    rent: number;
    dividends: number;
  };
}

// Offering Ratings
export interface IRatingItem {
  title?: string;
  type?: string;
  value?: string | number;
  color?: string;
  description?: string;
}

export type IRatingList = Array<IRatingItem>;

// Tables
export type tablesType = tablesList | tablesMap;
export type tablesList = Array<ITableData>;
export type tablesMap = {
  [key: string]: ITableData;
};

// Description Item
export type IContentItem = {
  title: string;
  description?: string;
  media_name?: string;
  media?: IResourceData;
  link?: string;
  style?: string;
};

export type IContentItemList = Array<IContentItem>;

export type ISponsorData = {
  id?: string;
  name?: string;
  creator_id?: string;
  logo?: string;
};

export type ISponsorInfo = {
  name?: string;
  description?: string;
  overview?: string;
  website?: string;
  logo?: IResourceData;
};

export type IKeyValueData = {
  [key: string]: string;
};

export interface IAmenities {
  [label: string]: Array<string>;
}

export type IFinancialSummary = {
  financial_details_title?: string;
  financial_details?: {
    [key: string]: string;
  };
  financial?: Record<string, unknown>[];
  financial_title?: string;
};

// Comment
export interface IComment {
  post_id: string;
  parent_id?: string;
  message: string;
  display_name: string;
  created_at: number;
}

export interface IMenuItem {
  name: string;
  url: string;
  basePath?: string;
  icon?: string;
  JsxIcon?: React.ElementType;
  action?: string;
  newTab?: boolean;
  auth?: boolean;
  approved?: boolean;
  system?: boolean;
  management?: boolean;
  className?: string;
}

export interface INavItem extends IMenuItem {
  narrow?: boolean;
  transparent?: boolean;
  dark?: boolean;
  otherUrls?: string[];
  menuItems?: IMenuItem[];
}

export type IHeaderConfig = {
  narrow?: boolean;
  transparent?: boolean;
  dark?: boolean;
  sideBar?: boolean;
  isFixed?: boolean;
  isBrowsing?: boolean;
  isPortfolio?: boolean;
  isCompoundHeader?: boolean;
  transformation?: 'offering';
};

export type IDashboardFilter = {
  name: string;
  filterType: string;
};

export type ViewModeCookieName = 'assetBrowsingView' | 'portfolioBrowsingView';
